import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query"
import {
	InfinitivePage,
	SuccessCursorResponse,
	SuccessResponse,
	useHttpClient,
} from "api/api"
import {
	ChatMessage,
	MessagePayload,
	MessageStatus,
} from "interface/communications"
import { User } from "interface/user"
import { useInvalidateOnRealtimeEvent } from "modules/realtime/useInvalidateOnRealtimeEvent"
import { useMemo } from "react"
import { asDate, today } from "utils/date"
import { getBase64 } from "utils/file"
import { v4 as uuidv4 } from "uuid"
import { useUserRole } from "./users"

export const queryKeys = {
	messages: "chat-messages",
	latestMessage: "chat-latest-message",
	messagesUnread: "chat-messages-unread",
	outbox: "chat-outbox",
	token: "chat-token",
}

export const useOutboxChatMessages = (
	patientId?: string,
	alternateContactId?: string | null,
) => {
	const queryClient = useQueryClient()
	const queryKeyOutbox = useMemo(
		() => [queryKeys.outbox, patientId, alternateContactId],
		[alternateContactId, patientId],
	)
	const remove = async (id?: string) => {
		await queryClient.setQueryData(
			queryKeyOutbox,
			(outboxMessages: ChatMessage[]) =>
				outboxMessages.filter(
					!!id
						? (message) => message.id !== id
						: (message) => message.status === MessageStatus.ERROR,
				),
		)
	}
	const updateStatus = async (id: string, status: MessageStatus) =>
		await queryClient.setQueryData(
			queryKeyOutbox,
			(outboxMessages: ChatMessage[]) =>
				outboxMessages.map((message) =>
					message.id === id ? { ...message, status } : message,
				),
		)
	const add = async ({
		id,
		text,
		sender,
		receiver,
		toType,
		mediaUrl,
		mediaOriginalName,
		type,
		subject,
		inReplyTo,
	}: { id: string; sender: User } & Pick<
		ChatMessage,
		| "text"
		| "receiver"
		| "toType"
		| "mediaUrl"
		| "mediaOriginalName"
		| "type"
		| "subject"
		| "inReplyTo"
	>) => {
		queryClient.setQueryData(
			queryKeyOutbox,
			(outboxMessages: ChatMessage[]) => [
				...outboxMessages,
				{
					id,
					sender: sender.id,
					senderInfo: {
						userType: sender.role,
						fullName: sender.fullName,
					},
					receiver,
					type,
					timestamp: today().toISOString(),
					isRead: false,
					status: MessageStatus.SENT,
					text,
					fromType: "User",
					toType,
					mediaUrl,
					mediaOriginalName,
					subject,
					inReplyTo,
				},
			],
		)
	}
	const { data } = useQuery<ChatMessage[]>({
		queryKey: queryKeyOutbox,
		queryFn: () => [],
		enabled: false,
		gcTime: Infinity,
		staleTime: Infinity,
		initialData: [],
	})

	return { data, add, remove, updateStatus }
}

export const useInfiniteChatMessages = (
	patientId?: string,
	alternateContactId?: string,
	threadId?: string | null,
) => {
	const { httpClient } = useHttpClient()
	const outbox = useOutboxChatMessages(patientId, alternateContactId)
	const queryKeyServer = useMemo(
		() => [queryKeys.messages, patientId, alternateContactId, threadId],
		[alternateContactId, patientId, threadId],
	)

	useInvalidateOnRealtimeEvent("unread_count_update", {
		queryKey: queryKeyServer,
		predicate: (data) => data.patientId === patientId,
		onInvalidate: async () => {
			await outbox.remove()
		},
	})

	return useInfiniteQuery<
		InfinitivePage<ChatMessage>,
		Error,
		{ pages: ChatMessage[] }
	>({
		queryKey: queryKeyServer,
		queryFn: async ({ pageParam, signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessCursorResponse<ChatMessage>>(
				`/api/v2/communications/users/${patientId}/all-messages`,
				{
					signal,
					params: {
						limit: 20,
						cursor: JSON.stringify(pageParam),
						alternateContactId,
						threadId,
					},
				},
			)

			return {
				data: data.items,
				nextCursor: data.nextCursor,
			}
		},
		enabled: !!patientId,
		initialPageParam: undefined,
		select: ({ pages }) => {
			return {
				pages: [...pages.flatMap(({ data }) => data), ...outbox.data].toSorted(
					(a, b) => asDate(a.timestamp).unix() - asDate(b.timestamp).unix(),
				),
			}
		},
		getNextPageParam: (lastPage) => lastPage.nextCursor,
		placeholderData: keepPreviousData,
	})
}

export const useFindLatestChatMessages = (patientId?: string) => {
	const { httpClient } = useHttpClient()
	const queryKey = useMemo(
		() => [queryKeys.latestMessage, patientId],
		[patientId],
	)

	useInvalidateOnRealtimeEvent("unread_count_update", {
		queryKey,
		predicate: (data) => data.patientId === patientId,
	})

	return useQuery({
		queryKey,
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<ChatMessage[]>>(
				`/api/v2/communications/users/${patientId}/latest-messages`,
				{
					signal,
				},
			)
		},
		enabled: !!patientId,
		select({ data }) {
			return data.data
		},
	})
}

export const useSendMessage = ({
	sender,
	patientId,
	receiver,
	toType,
}: { sender: User; patientId: string } & Pick<
	ChatMessage,
	"receiver" | "toType"
>) => {
	const { httpClient } = useHttpClient()
	const alternateContactId = receiver === patientId ? undefined : receiver
	const outbox = useOutboxChatMessages(patientId, alternateContactId)

	return useMutation({
		mutationKey: ["chat-send-message"],
		mutationFn: (params: MessagePayload) =>
			httpClient.post(
				`/api/v2/communications/users/${patientId}/message`,
				{
					receiver,
					toType,
					...params,
				},
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				},
			),
		onMutate: async ({ text, id, file, ...rest }) => {
			const optimisticMessageId = uuidv4()

			await outbox.remove(id)

			await outbox.add({
				id: optimisticMessageId,
				text: text ?? "",
				sender,
				toType,
				receiver,
				mediaUrl: file ? await getBase64(file) : undefined,
				mediaOriginalName: file ? file.name : undefined,
				...rest,
			})

			return { id: optimisticMessageId }
		},
		onError: async (_error, _variables, onMutateResult) => {
			if (!!onMutateResult?.id) {
				await outbox.updateStatus(onMutateResult.id, MessageStatus.ERROR)
			}
		},
	})
}

export const useRetryMessage = (patientId: string) => {
	const { httpClient } = useHttpClient()

	return useMutation({
		mutationKey: ["chat-retry-message"],
		mutationFn: (messageId: string) =>
			httpClient.post(
				`/api/v2/communications/users/${patientId}/sms/${messageId}/retry`,
			),
	})
}

export const useMarkAllMessagesAsRead = (patientId?: string) => {
	const { httpClient } = useHttpClient()

	return useMutation({
		mutationKey: ["chat-mark-all-messages-as-read"],
		mutationFn: (messageId: string) =>
			httpClient.patch(
				`/api/v2/communications/users/${patientId}/mark-all-messages-as-read`,
				{ messageId },
			),
	})
}

export const useUnreadMessagesCount = (patientId?: string) => {
	const { httpClient } = useHttpClient()
	const queryKey = useMemo(
		() => [queryKeys.messagesUnread, patientId],
		[patientId],
	)

	useInvalidateOnRealtimeEvent("unread_count_update", {
		queryKey,
		predicate: (data) => data.patientId === patientId,
	})

	return useQuery({
		queryKey,
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<{ unreadMessagesCount: number }>>(
				`/api/v2/communications/users/${patientId}/unread-messages-count`,
				{
					signal,
				},
			)
		},
		enabled: !!patientId,
		select({ data }) {
			return data.data.unreadMessagesCount
		},
	})
}

export const useChatToken = (enabled = true) => {
	const { httpClient } = useHttpClient()
	const role = useUserRole()

	return useQuery({
		queryKey: [queryKeys.token, role.data?.id],
		queryFn({ signal }) {
			return httpClient.get<
				SuccessResponse<{ identity: string; token: string }>
			>(`/api/v2/communications/users/${role.data?.id}/token`, {
				signal,
			})
		},
		enabled: enabled && role.isEnabled && !!role.data?.id,
		staleTime: 3_600_000, // 1 hour
		refetchInterval: 50 * 60 * 1000, // Refetch every 50 minutes (tokens typically expire after 1 hour)
		refetchIntervalInBackground: true, // Continue refetching when tab is in background
		refetchOnWindowFocus: true, // Refetch when window gains focus
		refetchOnReconnect: true, // Refetch when reconnecting to network
		retry: 3, // Retry failed requests 3 times
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff with 30s max
		retryOnMount: true, // Retry on component mount if previous attempt failed
		select(response) {
			return response.data.data
		},
	})
}
